import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

const Ctx = createContext(null);
const KEY = "fg_last_project";

// One active project for the whole workspace: pick it once and it follows you across
// Design Engine, Validation, Drawing Studio, BOM, Documents and Analytics.
// A ?project= / ?project_id= query parameter always wins over the remembered one.
export function ProjectProvider({ children }) {
  const [projects, setProjects] = useState(null);
  const [pid, setPidState] = useState(() => localStorage.getItem(KEY) || "");
  const [params] = useSearchParams();
  const urlPid = params.get("project") || params.get("project_id") || "";

  const setPid = (id) => {
    setPidState(id || "");
    if (id) localStorage.setItem(KEY, id);
  };

  const reload = () =>
    api.get("/projects").then(({ data }) => { setProjects(data); return data; })
      .catch(() => { setProjects([]); return []; });

  useEffect(() => { reload(); }, []);
  useEffect(() => { if (urlPid && urlPid !== pid) setPid(urlPid); }, [urlPid]);

  // a project created in this session (or opened by link) may not be in the cached list
  useEffect(() => {
    if (!projects || !pid) return;
    if (!projects.some((p) => p.id === pid)) reload();
  }, [pid]); // eslint-disable-line

  useEffect(() => {
    if (!projects) return;
    if (pid && projects.some((p) => p.id === pid)) return;
    const best = projects.find((p) => p.industry === "power_transformers") || projects[0];
    if (best) setPid(best.id);
  }, [projects]);

  const value = useMemo(() => ({
    projects: projects || [], loading: projects === null,
    pid, setPid, reload,
    project: (projects || []).find((p) => p.id === pid) || null,
  }), [projects, pid]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useProject() {
  return useContext(Ctx) || { projects: [], loading: false, pid: "", setPid: () => {}, project: null, reload: () => {} };
}
